import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import {
  PlayArrow as ExecutionIcon,
  CheckCircle as SuccessIcon,
  Science as ExperimentIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { adjustOpacity } from '../../../theme/utils';

/**
 * SummaryCards Component
 *
 * Displays summary statistics cards for executions.
 *
 * @param {Object} props
 * @param {number} props.totalCount - Total number of executions
 * @param {Object} props.summaryStats - Summary statistics object
 * @param {number} props.summaryStats.successful_count - Number of successful executions
 * @param {number} props.summaryStats.failed_count - Number of failed executions
 * @param {number} props.summaryStats.experiment_count - Number of experiment executions
 */
function SummaryCards({ totalCount, summaryStats }) {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%', px: 14, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-evenly',
          alignItems: 'stretch',
          gap: 14,
        }}
      >
        {/* Total Executions Card */}
        <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: theme.palette.primary.contrastText,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.primary.main, 0.2)}`,
              transition: 'all 0.3s ease',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <ExecutionIcon
                  sx={{
                    mr: 0.75,
                    fontSize: '1.25rem',
                    color: theme.palette.primary.contrastText,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText,
                    fontSize: '0.9375rem',
                  }}
                >
                  Total Executions
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.contrastText,
                  fontSize: '1.75rem',
                  textAlign: 'center',
                }}
              >
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Success Rate Card */}
        <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              color: theme.palette.success.contrastText,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.success.main, 0.2)}`,
              transition: 'all 0.3s ease',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.success.main, 0.3)}`,
              },
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <SuccessIcon
                  sx={{
                    mr: 0.75,
                    fontSize: '1.25rem',
                    color: theme.palette.success.contrastText,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.success.contrastText,
                    fontSize: '0.9375rem',
                  }}
                >
                  Success Rate
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.success.contrastText,
                  fontSize: '1.75rem',
                  textAlign: 'center',
                }}
              >
                {totalCount > 0
                  ? Math.round(
                      (summaryStats.successful_count / totalCount) * 100
                    )
                  : 0}
                %
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Experiment Executions Card */}
        <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: theme.palette.secondary.contrastText,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${adjustOpacity(theme.palette.secondary.main, 0.2)}`,
              transition: 'all 0.3s ease',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 20px ${adjustOpacity(theme.palette.secondary.main, 0.3)}`,
              },
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <ExperimentIcon
                  sx={{
                    mr: 0.75,
                    fontSize: '1.25rem',
                    color: theme.palette.secondary.contrastText,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.secondary.contrastText,
                    fontSize: '0.9375rem',
                  }}
                >
                  Experiment Executions
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.secondary.contrastText,
                  fontSize: '1.75rem',
                  textAlign: 'center',
                }}
              >
                {summaryStats.experiment_count}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default SummaryCards;
